/**
 * iOS/WebKit standalone-PWA keyboard workarounds.
 *
 * WebKit will not raise the soft keyboard for a programmatic `focus()`
 * (243416). After Google / Face ID the next `focus()` also stays silent
 * (279904). The previous helper called `focus({ preventScroll: true })` on
 * every tap — iOS treated that as programmatic and never showed the keyboard,
 * including on a freshly installed home-screen icon.
 *
 * Do not focus, blur, or scroll from script. Let the browser handle the tap.
 * Only install CSS that stops `touch-action: manipulation` (set on the login
 * form) and 16px text (avoids iOS zoom-instead-of-keyboard).
 */

import { IOS_SAFE_BOTTOM_FALLBACK_PX, IOS_SAFE_TOP_FALLBACK_PX } from './theme.tokens'

const isBrowser = typeof window !== 'undefined' && typeof document !== 'undefined'

function isIosLike(): boolean {
  if (!isBrowser) return false
  const ua = navigator.userAgent || ''
  if (/iPad|iPhone|iPod/.test(ua)) return true
  return (
    (navigator.platform === 'MacIntel' || /Macintosh/.test(ua)) &&
    (navigator.maxTouchPoints || 0) > 1
  )
}

function isStandaloneDisplay(): boolean {
  if (!isBrowser) return false
  const nav = navigator as Navigator & { standalone?: boolean }
  return (
    window.matchMedia?.('(display-mode: standalone)').matches === true || nav.standalone === true
  )
}

export function isStandaloneIos(): boolean {
  return isIosLike() && isStandaloneDisplay()
}

/**
 * Notch / home-indicator floor for the installed app only.
 * A floor on every iPhone stacks on Safari's URL bar and bottom toolbar.
 */
export function iosSafeAreaMinVars(
  iosLike: boolean,
  standalone: boolean,
): { satMin: string; sabMin: string } | null {
  if (!iosLike || !standalone) return null
  return {
    satMin: `${IOS_SAFE_TOP_FALLBACK_PX}px`,
    sabMin: `${IOS_SAFE_BOTTOM_FALLBACK_PX}px`,
  }
}

/**
 * Mobile Safari already insets the page for its chrome. `env(safe-area-inset-*)`
 * with `viewport-fit=cover` still reports the notch and the bottom toolbar,
 * which leaves a gap above the app bar and stretches the tab bar.
 * `0px` wins over `env()` in the safe-area token. Do not set this in standalone.
 */
export function iosBrowserSafeAreaVars(
  iosLike: boolean,
  standalone: boolean,
): { safeTop: string; safeBottom: string } | null {
  if (!iosLike || standalone) return null
  return { safeTop: '0px', safeBottom: '0px' }
}

function applySafeAreaFallbacks(): void {
  if (!isBrowser) return
  const ios = isIosLike()
  const standalone = isStandaloneDisplay()
  const mins = iosSafeAreaMinVars(ios, standalone)
  const browser = iosBrowserSafeAreaVars(ios, standalone)
  const root = document.documentElement

  if (mins) {
    root.style.setProperty('--app-sat-min', mins.satMin)
    root.style.setProperty('--app-sab-min', mins.sabMin)
  } else {
    root.style.removeProperty('--app-sat-min')
    root.style.removeProperty('--app-sab-min')
  }

  if (browser) {
    root.style.setProperty('--app-safe-top', browser.safeTop)
    root.style.setProperty('--app-safe-bottom', browser.safeBottom)
  } else {
    root.style.removeProperty('--app-safe-top')
    root.style.removeProperty('--app-safe-bottom')
  }
}

const EDITABLE =
  'input:not([type=checkbox]):not([type=radio]):not([type=button]):not([type=submit]):not([type=reset]):not([type=file]):not([type=hidden]):not([type=range]):not([type=color]), textarea, [contenteditable="true"]'

let installed = false

/** Idempotent. CSS only — never calls focus/blur/scrollTo. */
export function installIosKeyboardFixes(): void {
  if (!isBrowser || installed) return
  installed = true

  const style = document.createElement('style')
  style.setAttribute('data-ios-keyboard-fix', '')
  style.textContent = [
    'form { touch-action: auto !important; }',
    `${EDITABLE} {`,
    '  touch-action: auto !important;',
    '  font-size: 16px !important;',
    '}',
  ].join('\n')
  document.head.appendChild(style)
  applySafeAreaFallbacks()

  if (!isStandaloneDisplay()) return

  const vv = window.visualViewport
  const root = document.documentElement
  const syncInset = () => {
    const inset = vv ? Math.max(0, window.innerHeight - vv.height - vv.offsetTop) : 0
    root.style.setProperty('--kb-inset', `${Math.round(inset)}px`)
  }

  vv?.addEventListener('resize', syncInset)
  vv?.addEventListener('scroll', syncInset)
  document.addEventListener('focusin', syncInset)
  document.addEventListener('focusout', syncInset)
  syncInset()
}
